import { InteractionRequiredAuthError, type AccountInfo } from '@azure/msal-browser';
import { msalInstance, silentRequest } from './msalConfig';

/// <summary>
/// Single writable ref holding the latest access token. The axios interceptor
/// reads from this ref on every request, so the token it sends always reflects
/// the last acquireTokenSilent call -- no closure captures a stale token.
/// Pattern mirrors IMS.
/// </summary>
export const tokenRef: { current: string | null } = { current: null };

// Error codes MSAL surfaces when the user needs to re-authenticate. Treated the
// same as InteractionRequiredAuthError: fall through to acquireTokenRedirect.
const INTERACTION_REQUIRED_CODES = new Set([
  'no_account_error',
  'login_required',
  'interaction_required',
  'consent_required'
]);

export async function acquireToken(account: AccountInfo | null): Promise<string | null> {
  const activeAccount = account ?? msalInstance.getActiveAccount();
  if (!activeAccount) return null;

  try {
    const result = await msalInstance.acquireTokenSilent({
      ...silentRequest,
      account: activeAccount
    });
    tokenRef.current = result.accessToken;
    return result.accessToken;
  } catch (err) {
    const code = (err as { errorCode?: string })?.errorCode;
    if (err instanceof InteractionRequiredAuthError || (code && INTERACTION_REQUIRED_CODES.has(code))) {
      // Fall back to redirect for mobile compatibility (iOS Safari popup quirks).
      tokenRef.current = null;
      await msalInstance.acquireTokenRedirect(silentRequest);
      return null;
    }
    // Network / ServerError / BrowserAuthError: clear the ref so callers
    // can decide whether to retry or surface; don't redirect on transient.
    tokenRef.current = null;
    throw err;
  }
}
